export const getVimeoId = (html: string): string | null => {
	const match = html.match(/https:\/\/player\.vimeo\.com\/video\/(\d+)/);
	return match?.[1] ?? null;
};

// SOUNDCLOUD EMBED IMPLEMENTATION
// const SOUNDCLOUD_URL =
// 	"https://soundcloud.com/akademia-ltca/34-ulga-na-innowacyjnych-pracownikow";
// export const getSoundCloudOEmbed = async () => {
// 	try {
// 		const response = await axios.post(
// 			"https://soundcloud.com/oembed",
// 			new URLSearchParams({
// 				format: "json",
// 				url: SOUNDCLOUD_URL,
// 			}),
// 			{
// 				headers: {
// 					"Content-Type": "application/x-www-form-urlencoded",
// 				},
// 			}
// 		);
// 		return response.data;
// 	} catch (error) {
// 		console.error("Error fetching oEmbed:", error);
// 	}
// };

export const checkForPrestitial = (): void => {
	const iframe = document.querySelector('iframe[src*="soundcloud"]');
	if (iframe) {
		try {
			// This will fail due to CORS, but you can detect the iframe
			const iframeDocument =
				iframe.contentDocument || iframe.contentWindow?.document;
			const prestitial = iframeDocument?.querySelector(".mobilePrestitial");
			if (prestitial) {
				// Hide the iframe or show alternative content
				prestitial.style.display = "none";
			}
		} catch (event) {
			// CORS error - iframe is from different domain
			console.log("Cannot set display none to mobilePrestitial", event);
		}
	}
};

export function detectVideoSourceType(
	body: string
): "vimeo_source" | "akademia_source" | "unknown" {
	if (!body || typeof body !== "string") {
		return "unknown";
	}

	// Check for Vimeo patterns
	const vimeoPatterns = [
		/player\.vimeo\.com\/video/i,
		/vimeo\.com\/\d+/i,
		/iframe.*src.*vimeo/i,
		/player\.vimeo\.com\/api\/player\.js/i,
	];

	// Check for Akademia LTCA patterns
	const akademiaPatterns = [
		/\[embed\].*akademialtca\.pl.*\[\/embed\]/i,
		/akademialtca\.pl\/.*\.mp4/i,
		/akademialtca\.pl\/Webinaria/i,
	];

	// Test for Vimeo
	for (const pattern of vimeoPatterns) {
		if (pattern.test(body)) {
			return "vimeo_source";
		}
	}

	// Test for Akademia LTCA
	for (const pattern of akademiaPatterns) {
		if (pattern.test(body)) {
			return "akademia_source";
		}
	}

	return "unknown";
}

/**
 * Extracts Akademia LTCA video URL from body content
 */
export function getAkademiaVideoUrl(body: string): string | null {
	const akademiaUrlPattern = /\[embed\](.*akademialtca\.pl.*\.mp4)\[\/embed\]/i;
	const match = body.match(akademiaUrlPattern);
	if (match && !!match[1]) {
		// Clean up escaped slashes
		const cleanUrl = match[1].replace(/\\\//g, "/");
		// Replace the domain with proxy path
		return cleanUrl.replace("https://akademialtca.pl/", "/proxy/akademialtca/");
	}
	return null;
}

/**
 * Parses video body content and returns video source information
 */
export function parseVideoBody(
	body: string
):
	| { video_source: "vimeo_source"; vimeoId: number }
	| { video_source: "akademia_source"; videoUrl: string }
	| { video_source: "unknown" } {
	const sourceType = detectVideoSourceType(body);

	switch (sourceType) {
		case "vimeo_source": {
			const vimeoIdString = getVimeoId(body);
			if (vimeoIdString) {
				const vimeoId = parseInt(vimeoIdString, 10);
				if (!isNaN(vimeoId)) {
					return { video_source: "vimeo_source", vimeoId };
				}
			}
			return { video_source: "unknown" };
		}

		case "akademia_source": {
			const videoUrl = getAkademiaVideoUrl(body);
			if (videoUrl) {
				return { video_source: "akademia_source", videoUrl };
			}
			return { video_source: "unknown" };
		}

		default:
			return { video_source: "unknown" };
	}
}

// Create an async function to wait for duration
export const waitForDuration = async (player: {
	duration?: number;
}): Promise<number> => {
	return new Promise((resolve) => {
		const checkDuration = (): void => {
			if (player?.duration && player.duration > 0) {
				resolve(player.duration);
			} else {
				requestAnimationFrame(checkDuration);
			}
		};
		checkDuration();
	});
};

/**
 * Removes mobile prestitial BUTTONS from SoundCloud widget by modifying URL parameters (MOBILE WIDGET VERSION)
 */
export const removeSoundCloudPrestitial = (embedHtml: string): string => {
	if (!embedHtml) return embedHtml;

	// Parse the HTML to extract iframe src
	const iframeSourceMatch = embedHtml.match(/src=["']([^"']+)["']/);
	if (!iframeSourceMatch) return embedHtml;

	let originalSource = iframeSourceMatch[1];

	// Decode HTML entities
	originalSource = originalSource?.replace(/&amp;/g, "&");

	// Create URL object to manipulate parameters
	try {
		const url = new URL(originalSource || "");

		// Add parameters to disable mobile prestitial
		url.searchParams.set("mobile", "false");
		url.searchParams.set("buying", "false");
		url.searchParams.set("download", "false");
		url.searchParams.set("sharing", "false");
		url.searchParams.set("show_playcount", "false");
		url.searchParams.set("hide_related", "true");
		url.searchParams.set("show_comments", "false");
		url.searchParams.set("show_teaser", "false");
		url.searchParams.set("visual", "false");

		// Replace the src in the original HTML
		const newSource = url.toString().replace(/&/g, "&amp;");
		return embedHtml.replace(iframeSourceMatch[1] || "", newSource);
	} catch (error) {
		console.error("Error modifying SoundCloud URL:", error);
		return embedHtml;
	}
};

/**
 * Sprawdza czy element body zawiera link do FlexiQuiz i zwraca go
 * @param body - HTML string który może zawierać iframe z FlexiQuiz
 * @returns Link do FlexiQuiz lub undefined jeśli nie znaleziono
 */
export const extractFlexiQuizLink = (body: string): string | undefined => {
	if (!body || typeof body !== 'string') {
		return undefined;
	}

	// Regex do wyszukania src z iframe zawierającego flexiquiz.com
	const flexiQuizRegex = /src=["']([^"']*flexiquiz\.com[^"']*)["']/i;
	const match = body.match(flexiQuizRegex);
	
	if (match && match[1]) {
		return match[1];
	}
	
	return undefined;
};
